import { useEffect, useRef } from "react";
import { Field } from "../../components/Field";
import { HIERARCHY_LEVEL_OPTIONS } from "./hierarchyLevels";

interface SegmentEditFormProps {
  segmentId: string;
  nombre: string;
  nivelJerarquico: string[];
  industria: string;
  error: string | null;
  pending: boolean;
  onNombreChange: (value: string) => void;
  onToggleNivel: (value: string) => void;
  onIndustriaChange: (value: string) => void;
  onSubmit: (event: React.FormEvent) => void;
  onCancel: () => void;
}

/**
 * Inline edit panel for a segment's nombre/criterio (panel-segment-crud). Mirrors
 * `TemplateReplaceEditor`'s convention: all state (`nombre`/`nivelJerarquico`/`industria`/
 * `error`/`pending`) stays owned by `SegmentDetailScreen` — this component is a plain
 * controlled panel. Never resets the typed input on error, only on success/cancel (same
 * "typed-input-preservation-on-error" contract as the templates feature).
 *
 * Codex automated review on commit c39dcc6 (P2 de accesibilidad, confirmado por el
 * orquestador leyendo el codigo real): `SegmentDetailScreen` only mounts this component
 * while `isEditing` is true (`{isEditing && <SegmentEditForm .../>}`), so a plain
 * mount-effect below is enough to move focus to the nombre input every time the editor
 * opens — no `isEditing` prop is needed here for that purpose.
 */
export function SegmentEditForm({
  segmentId,
  nombre,
  nivelJerarquico,
  industria,
  error,
  pending,
  onNombreChange,
  onToggleNivel,
  onIndustriaChange,
  onSubmit,
  onCancel,
}: SegmentEditFormProps) {
  const nombreInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    nombreInputRef.current?.focus();
  }, []);

  return (
    <form onSubmit={onSubmit} aria-label="Editar segmento">
      <Field label="Nombre" htmlFor={`segment-edit-nombre-${segmentId}`}>
        <input
          id={`segment-edit-nombre-${segmentId}`}
          ref={nombreInputRef}
          className="v2-u-target-min"
          value={nombre}
          onChange={(e) => onNombreChange(e.target.value)}
          disabled={pending}
          required
        />
      </Field>

      {/* User follow-up 2026-09-16: same collapsed-by-default disclosure as
          SegmentCreateForm.tsx — keeps multi-select without the always-open
          fieldset's vertical space. */}
      <details className="v2-u-checkbox-dropdown">
        <summary className="v2-u-target-min">
          Nivel jerárquico{nivelJerarquico.length > 0 ? ` (${nivelJerarquico.length})` : ""}
        </summary>
        <fieldset>
          <legend>Nivel jerárquico</legend>
          {HIERARCHY_LEVEL_OPTIONS.map((option) => (
            <label key={option.value} className="v2-u-target-min">
              <input
                type="checkbox"
                checked={nivelJerarquico.includes(option.value)}
                onChange={() => onToggleNivel(option.value)}
                disabled={pending}
              />
              {option.label}
            </label>
          ))}
        </fieldset>
      </details>

      <Field label="Industria" htmlFor={`segment-edit-industria-${segmentId}`}>
        <input
          id={`segment-edit-industria-${segmentId}`}
          className="v2-u-target-min"
          value={industria}
          onChange={(e) => onIndustriaChange(e.target.value)}
          disabled={pending}
        />
      </Field>

      {error && <p role="alert">{error}</p>}

      <button type="submit" className="v2-u-target-min" disabled={pending} aria-busy={pending}>
        {pending ? "Guardando…" : "Guardar cambios"}
      </button>
      <button type="button" className="v2-u-target-min" onClick={onCancel} disabled={pending}>
        Cancelar
      </button>
    </form>
  );
}
